import { useState, useEffect, useCallback, useRef } from 'react';

// Stale-while-revalidate caching for list pages.
//
// Why this exists: Azure SQL is on the Basic tier (5 DTUs), where a bare
// SELECT COUNT(*) over ~435 rows measures ~370ms. Re-running the same query
// every time a user flips a filter back and forth is the single biggest source
// of "the page feels frozen" on Sales Orders and PO Tracker. This does NOT
// make the database faster — upgrading the tier is the real lever. It only
// stops us asking the same question twice in a row.
//
// Stale-while-revalidate rather than a plain TTL cache because these are
// mutable financial records: another user can cancel or invoice an SO at any
// moment. So a cache hit renders instantly AND kicks off a background refetch,
// which means staleness exposure is about a second, not the length of the TTL.
//
// Deliberately in-memory only — never sessionStorage/localStorage. A browser
// reload is the universal "give me the current numbers" gesture, and keeping
// this in memory guarantees reload is always clean.
//
// Hand-rolled rather than adding TanStack Query: this frontend has 12
// dependencies, `npm test` is already broken by a dependency conflict, and a
// missing package took production down. If caching spreads to more pages,
// adopting TanStack Query deliberately across the app beats growing this.

export const DEFAULT_STALE_MS = 30_000;

/**
 * A bounded LRU cache of `{ data, fetchedAt }`, one per page (not a module-level
 * singleton — so leaving a page and coming back gets fresh data, and two pages
 * can't invalidate each other by accident).
 *
 * `max` matters: a Sales Orders entry holds 50 rows, but a PO Tracker entry can
 * hold up to 2000 (po-tracker.js's row cap), so the two pages want very
 * different bounds for the same amount of memory.
 */
export function useSwrStore(max = 30) {
  const ref = useRef(null);
  if (ref.current === null) ref.current = createSwrStore(max);
  return ref.current;
}

export function createSwrStore(max = 30) {
  const map = new Map(); // insertion-ordered, so the first key is the LRU victim

  return {
    /** Read without affecting recency — used for the initial synchronous render. */
    peek(key) {
      return map.get(key) || null;
    },

    /** Read and mark most-recently-used. `fresh` is false once past `staleMs`. */
    get(key, staleMs = DEFAULT_STALE_MS) {
      const entry = map.get(key);
      if (!entry) return null;
      map.delete(key);
      map.set(key, entry);
      return { ...entry, fresh: Date.now() - entry.fetchedAt < staleMs };
    },

    set(key, data) {
      map.delete(key);
      map.set(key, { data, fetchedAt: Date.now() });
      while (map.size > max) map.delete(map.keys().next().value);
    },

    /**
     * Drop everything. Mutations must use this rather than evicting a single
     * key: cancelling an SO changes the row set AND the counts of several
     * filters at once, and editing a PO's date can move it out of the range
     * you're looking at and into a different cached one. Selective
     * invalidation would leave those other entries showing pre-mutation data.
     */
    clear() {
      map.clear();
    },

    size() {
      return map.size;
    },
  };
}

/**
 * Runs `fetcher` for `key`, serving the cached value first when there is one.
 *
 * `key` should BE the query string of the request (not a hand-built label), and
 * `fetcher` should build its URL from that same string. That way the key and
 * the request it stands for physically cannot drift apart — a key that forgot
 * to include one filter is the classic way a cache like this serves the wrong
 * rows.
 *
 * `fetcher` must resolve to the value to cache and throw on failure.
 *
 * Returns `{ data, loading, error, fetchedAt, refresh }`.
 */
export function useSwrQuery(store, key, fetcher, options = {}) {
  const { staleMs = DEFAULT_STALE_MS, enabled = true, revalidateOnFocus = true } = options;

  const [state, setState] = useState(() => {
    const hit = store.peek(key);
    return hit
      ? { data: hit.data, loading: false, error: '', fetchedAt: hit.fetchedAt }
      : { data: undefined, loading: enabled, error: '', fetchedAt: 0 };
  });

  // Held in a ref so callers can pass an inline arrow function without every
  // render counting as a changed dependency and refetching in a loop.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Out-of-order response guard. Flipping filters quickly can leave an earlier,
  // slower request resolving after a later one; only the newest token applies.
  // This matters more with SWR than without it, because a background
  // revalidation of the OLD key can still be in flight when the new key's
  // foreground request lands, and it must not overwrite it.
  const token = useRef(0);
  const fetchedAtRef = useRef(0);

  const run = useCallback(
    async (background) => {
      const t = ++token.current;
      if (!background) setState((s) => ({ ...s, loading: true, error: '' }));
      try {
        const data = await fetcherRef.current();
        if (t !== token.current) return; // superseded — discard
        store.set(key, data);
        const fetchedAt = Date.now();
        fetchedAtRef.current = fetchedAt;
        setState({ data, loading: false, error: '', fetchedAt });
      } catch (err) {
        if (t !== token.current) return;
        if (background) {
          // A failed background revalidation must not blank out the rows the
          // user is currently reading. Keep the stale data and keep the old
          // fetchedAt — the data really is that old, and the age indicator
          // showing it climbing is the honest signal here.
          setState((s) => ({ ...s, loading: false }));
          return;
        }
        setState({ data: undefined, loading: false, error: err.message || 'Request failed', fetchedAt: 0 });
      }
    },
    [store, key]
  );

  useEffect(() => {
    if (!enabled) return;
    const hit = store.get(key, staleMs);
    if (!hit) {
      run(false); // miss — normal fetch, with the page's loading state showing
      return;
    }
    fetchedAtRef.current = hit.fetchedAt;
    setState({ data: hit.data, loading: false, error: '', fetchedAt: hit.fetchedAt });
    if (!hit.fresh) run(true); // stale — render it now, refresh behind it
  }, [store, key, staleMs, enabled, run]);

  // Same trigger AccountsLayout.jsx already uses for its sidebar badges. The
  // staleMs check is what keeps rapid tab-flipping from firing a burst of
  // requests, so no separate debounce is needed.
  useEffect(() => {
    if (!enabled || !revalidateOnFocus) return;
    const onFocus = () => {
      if (Date.now() - fetchedAtRef.current >= staleMs) run(true);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, revalidateOnFocus, staleMs, run]);

  const refresh = useCallback(() => run(false), [run]);

  return { ...state, refresh };
}
